import React from 'react';
import {
  CheckCircle, XCircle, Play, Pause, Eye, Pen, History, Trash2, UserCog
} from 'lucide-react';

const TicketMobileActionBar = ({
  ticket, hasRole, hasPermission, onQuickAccept, onQuickStatusChange, onReject, onReassign,
  onOpenMobileWizard, onRevise, onDelete
}) => {
  const isEditable = !['closed', 'transformed'].includes(ticket.status);
  if (!isEditable && ticket.status !== 'closed') return null;

  const canWrite = hasPermission ? hasPermission('tickets.write') : hasRole('manager', 'admin', 'technician');
  const canAssign = hasPermission ? hasPermission('tickets.assign') : hasRole('manager', 'admin');
  const canClose = hasPermission ? hasPermission('tickets.close') : hasRole('manager', 'admin', 'technician');
  const canDelete = hasPermission ? hasPermission('tickets.delete') : hasRole('admin');

  return (
    <div
      className="lg:hidden fixed bottom-[56px] left-0 right-0 z-40 bg-white border-t shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-3 py-2.5 flex gap-2"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-action-bar"
    >
      {ticket.status === 'closed' && canWrite && (
        <Btn className="bg-amber-600 text-white active:bg-amber-700" onClick={onRevise} testId="mobile-revise-btn">
          <History className="w-4 h-4" /> Revizuieste
        </Btn>
      )}

      {ticket.status === 'open' && canWrite && (
        <>
          <Btn className="bg-emerald-600 text-white active:bg-emerald-700" onClick={onQuickAccept} testId="mobile-accept-btn">
            <CheckCircle className="w-4 h-4" /> Accepta
          </Btn>
          <Btn className="text-red-600 border border-red-200 active:bg-red-50" onClick={onReject} flex={false}>
            <XCircle className="w-4 h-4" /> Respinge
          </Btn>
        </>
      )}

      {ticket.status === 'accepted' && canWrite && (
        <>
          <Btn className="bg-blue-600 text-white active:bg-blue-700" onClick={() => onQuickStatusChange('in_progress')} testId="mobile-start-work-btn">
            <Play className="w-4 h-4" /> Incepe Lucrul
          </Btn>
          {canClose && (
            <Btn className="bg-slate-800 text-white active:bg-slate-900" onClick={onOpenMobileWizard} testId="mobile-close-btn">
              <Pen className="w-4 h-4" /> Inchide
            </Btn>
          )}
        </>
      )}

      {ticket.status === 'in_progress' && canWrite && (
        <>
          <Btn className="bg-emerald-600 text-white active:bg-emerald-700" onClick={() => onQuickStatusChange('resolved')} testId="mobile-resolved-btn">
            <CheckCircle className="w-4 h-4" /> Rezolvat
          </Btn>
          <Btn className="border border-yellow-400 text-yellow-700 active:bg-yellow-50" onClick={() => onQuickStatusChange('monitoring')} flex={false} testId="mobile-monitoring-btn">
            <Eye className="w-4 h-4" />
          </Btn>
          <Btn className="border border-orange-300 text-orange-700 active:bg-orange-50" onClick={() => onQuickStatusChange('waiting_parts')} flex={false}>
            <Pause className="w-4 h-4" />
          </Btn>
          {canClose && (
            <Btn className="bg-slate-800 text-white active:bg-slate-900" onClick={onOpenMobileWizard} flex={false}>
              <Pen className="w-4 h-4" />
            </Btn>
          )}
        </>
      )}

      {ticket.status === 'monitoring' && canWrite && (
        <>
          <Btn className="bg-blue-600 text-white active:bg-blue-700" onClick={() => onQuickStatusChange('in_progress')}>
            <Play className="w-4 h-4" /> Reia Lucrul
          </Btn>
          <Btn className="bg-emerald-600 text-white active:bg-emerald-700" onClick={() => onQuickStatusChange('resolved')}>
            <CheckCircle className="w-4 h-4" /> Rezolvat
          </Btn>
          {canClose && (
            <Btn className="bg-slate-800 text-white active:bg-slate-900" onClick={onOpenMobileWizard} flex={false}>
              <Pen className="w-4 h-4" />
            </Btn>
          )}
        </>
      )}

      {ticket.status === 'waiting_parts' && canWrite && (
        <>
          <Btn className="bg-blue-600 text-white active:bg-blue-700" onClick={() => onQuickStatusChange('in_progress')}>
            <Play className="w-4 h-4" /> Reia Lucrul
          </Btn>
          <Btn className="bg-emerald-600 text-white active:bg-emerald-700" onClick={() => onQuickStatusChange('resolved')}>
            <CheckCircle className="w-4 h-4" /> Rezolvat
          </Btn>
          {canClose && (
            <Btn className="bg-slate-800 text-white active:bg-slate-900" onClick={onOpenMobileWizard} flex={false}>
              <Pen className="w-4 h-4" />
            </Btn>
          )}
        </>
      )}

      {ticket.status === 'resolved' && canClose && (
        <Btn className="bg-slate-800 text-white active:bg-slate-900" onClick={onOpenMobileWizard}>
          <Pen className="w-4 h-4" /> Inchide cu Semnatura
        </Btn>
      )}

      {!['closed', 'transformed', 'rejected', 'open'].includes(ticket.status) && canAssign && (
        <Btn className="border border-slate-300 text-slate-600 active:bg-slate-50" onClick={onReassign} flex={false} testId="mobile-reassign-btn">
          <UserCog className="w-4 h-4" />
        </Btn>
      )}

      {canDelete && (
        <Btn className="text-red-500 border border-red-200 active:bg-red-50" onClick={onDelete} flex={false}>
          <Trash2 className="w-4 h-4" />
        </Btn>
      )}
    </div>
  );
};

const Btn = ({ children, className = '', onClick, testId, flex = true }) => (
  <button
    className={`${flex ? 'flex-1' : ''} flex items-center justify-center gap-1.5 text-xs font-semibold py-3 ${flex ? '' : 'px-3'} rounded-lg ${className}`}
    onClick={onClick}
    data-testid={testId}
  >
    {children}
  </button>
);

export default TicketMobileActionBar;
